import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useToast } from 'vue-toastification'
import api from '../utils/api'

const toast = useToast()
const MAX_ACTIVITY_ENTRIES = 200
const ACTIVITY_STORAGE_PREFIX = 'bwai_activity_'

export const useSitesStore = defineStore('sites', () => {
  const sites = ref([])
  const currentSite = ref(null)
  const updateRequests = ref([])
  const loading = ref(false)
  const sseConnection = ref(null)
  
  // User/tenant state
  const user = ref(null)
  const tenant = ref(null)

  // Release history + rollback state (Commit-Selectable Release History)
  const releaseHistory = ref(null)            // { releases, liveCommitSha, liveCommitInHistory, productionBranch }
  const releaseHistoryLoading = ref(false)
  const releaseHistoryError = ref(null)       // { code, message } when AC010 provider failure
  const activeRollbackJob = ref(null)         // last requested/observed rollback job

  // Edit-lock state (Edit Locking for Concurrent Update Safety AC009/AC010/AC012)
  // Shape: { locked, isOwner, pendingCount, lock: { ownerEmail, ownerUserId, expiresAt, ... } | null }
  const inlineEditLockStatus = ref(null)
  const inlineEditLockLoading = ref(false)
  const inlineEditLockError = ref(null)
  // Last lock-conflict error from a mutation; UI uses this to render an
  // actionable message after a 409 (AC012).
  const lastLockConflict = ref(null)          // { code, heldByEmail, expiresAt, entryPoint, message }
  // AC001+AC002: load main-branch releases for the current site. Failures are
  // stored as a non-destructive error envelope so the UI can render an explicit
  // error state with retry capability (AC010), instead of throwing here.
  // AC010: a failing refresh must NOT erase the previously-loaded release
  // history — we preserve the last-known data so the operator can see prior
  // commits while we tell them the refresh failed.
  const fetchReleaseHistory = async (siteId) => {
    if (!siteId) return null
    releaseHistoryLoading.value = true
    releaseHistoryError.value = null
    try {
      const res = await api.get(`/sites/${siteId}/releases`)
      releaseHistory.value = res.data
      return res.data
    } catch (err) {
      const body = err.response?.data || {}
      releaseHistoryError.value = {
        code: body.code || 'unknown_error',
        message: body.error || err.message || 'Failed to load release history',
      }
      // Intentionally do NOT clear releaseHistory.value here — keeping the
      // last successful snapshot lets the UI render prior rows alongside the
      // error banner. Rollback controls are gated separately on the error
      // state so partial-data attacks can't trigger an action.
      return null
    } finally {
      releaseHistoryLoading.value = false
    }
  }
  // AC003+AC004: kicks off a rollback. The caller is responsible for showing
  // the explicit confirmation modal before invoking this. The server returns
  // 202 + a job id; we poll AC008 status via fetchReleaseJob.
  const requestRollback = async (siteId, targetCommitSha) => {
    const res = await api.post(`/sites/${siteId}/rollback`, { targetCommitSha })
    activeRollbackJob.value = res.data?.job || null
    return res.data
  }
  const fetchReleaseJob = async (jobId) => {
    if (!jobId) return null
    const res = await api.get(`/release-jobs/${jobId}`)
    activeRollbackJob.value = res.data?.job || null
    return res.data?.job || null
  }

  // Computed
  const activeSites = computed(() => 
    sites.value.filter(site => site.status === 'active')
  )

  const creatingSites = computed(() => 
    sites.value.filter(site => site.status === 'creating')
  )

  // Actions
  const fetchSites = async () => {
    loading.value = true
    try {
      const response = await api.get('/sites')
      sites.value = response.data.sites
      return response.data
    } catch (error) {
      console.error('Failed to fetch sites:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const fetchSite = async (siteId) => {
    try {
      const response = await api.get(`/sites/${siteId}`)
      currentSite.value = response.data.site
      return response.data.site
    } catch (error) {
      console.error('Failed to fetch site:', error)
      throw error
    }
  }

  const refreshSitePreview = async (siteId, options = {}) => {
    const { silent = false } = options
    try {
      const response = await api.post(`/sites/${siteId}/preview/refresh`)
      const updatedSite = response.data.site
      const restarting = Boolean(response.data.restarting)

      if (updatedSite) {
        const siteIndex = sites.value.findIndex(site => site.id === updatedSite.id)
        if (siteIndex !== -1) {
          sites.value[siteIndex] = updatedSite
        }
        if (currentSite.value?.id === updatedSite.id) {
          currentSite.value = updatedSite
        }
      } else if (response.data.previewUrl) {
        updateSitePreviewUrl(siteId, response.data.previewUrl)
      }

      if (!silent) {
        if (restarting) {
          toast.info('Preview server is starting — this may take up to a minute')
        } else if (response.data.previewUrl) {
          toast.success('Preview URL refreshed')
        } else {
          toast.info('Preview URL not available yet')
        }
      }

      return { site: updatedSite || currentSite.value, restarting }
    } catch (error) {
      console.error('Failed to refresh preview:', error)
      if (!silent) {
        toast.error('Failed to refresh preview URL')
      }
      throw error
    }
  }

  // Run preflight duplicate check before a repo import.
  // Uses a short 10 s timeout so a hung server surfaces quickly rather than
  // blocking the user silently. Throws on any failure so the caller can show
  // an actionable error and block import creation.
  const preflightRepoImport = async (repositoryUrl) => {
    try {
      const response = await api.get('/sites/preflight/repo-import', {
        params: { repositoryUrl },
        timeout: 10_000,
      })
      return response.data
    } catch (error) {
      // Surface a clear, retryable error — never swallow so the caller can block import
      if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
        throw new Error('Duplicate check timed out. Please retry.')
      }
      const msg = error.response?.data?.error || 'Duplicate check failed. Please retry.'
      throw new Error(msg)
    }
  }

  const discoverClonePages = async (cloneUrl) => {
    const response = await api.post('/sites/clone/discover', { cloneUrl })
    return response.data
  }

  // In-flight dedupe map: prevents double-submit when "Continue Anyway" is clicked
  // on a slow network. Key = "import:<repositoryUrl>" for repo imports, else "<creationType>".
  // Cleared in finally so subsequent distinct attempts always work.
  const _inflight = new Map()

  const createSite = async (siteData, creationType) => {
    const dedupeKey = creationType === 'import' && siteData.repositoryUrl
      ? `import:${siteData.repositoryUrl}`
      : creationType

    if (_inflight.has(dedupeKey)) {
      // Request already in-flight for this repo URL — return the same promise
      return _inflight.get(dedupeKey)
    }

    loading.value = true
    const request = api.post(`/sites/create/${creationType}`, siteData)
      .then((response) => {
        const newSite = response.data.site
        sites.value.unshift(newSite)
        toast.success(response.data.message)
        return response.data
      })
      .catch((error) => {
        console.error('Failed to create site:', error)
        throw error
      })
      .finally(() => {
        loading.value = false
        _inflight.delete(dedupeKey)
      })

    _inflight.set(dedupeKey, request)
    return request
  }

  const createHtmlImportSite = async (siteData, file) => {
    const dedupeKey = `htmlImport:${siteData.name}`
    if (_inflight.has(dedupeKey)) return _inflight.get(dedupeKey)

    loading.value = true
    const formData = new FormData()
    formData.append('name', siteData.name)
    if (siteData.customDomain) formData.append('customDomain', siteData.customDomain)
    formData.append('file', file)

    const request = api.post('/sites/create/html_import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
      .then((response) => {
        const newSite = response.data.site
        sites.value.unshift(newSite)
        toast.success(response.data.message || 'HTML import started')
        return response.data
      })
      .catch((error) => {
        console.error('Failed to create HTML import site:', error)
        throw error
      })
      .finally(() => {
        loading.value = false
        _inflight.delete(dedupeKey)
      })

    _inflight.set(dedupeKey, request)
    return request
  }

  // Per-page SEO / AEO / GEO
  const fetchPageSeo = async (siteId) => {
    const { data } = await api.get(`/sites/${siteId}/seo/pages`)
    return data
  }
  const savePageSeo = async (siteId, path, seo) => {
    const { data } = await api.put(`/sites/${siteId}/seo/pages`, { path, seo })
    return data
  }

  const createUpdateRequest = async (siteId, updateData, { quiet = false } = {}) => {
    try {
      const response = await api.post(`/sites/${siteId}/update`, updateData)

      // Quiet mode (element-scoped AI edits): skip the optimistic list insert
      // and the success toast so the edit stays a silent, right-panel action.
      if (!quiet && currentSite.value?.id === siteId) {
        updateRequests.value.unshift(response.data.updateRequest)
      }

      if (!quiet) toast.success(response.data.message)
      return response.data
    } catch (error) {
      console.error('Failed to create update request:', error)
      _captureLockConflict(error, 'update')
      throw error
    }
  }

  const submitInlineEdits = async (siteId, payload) => {
    try {
      const response = await api.post(`/sites/${siteId}/inline-edits`, payload)
      if (currentSite.value?.id === siteId && response.data?.updateRequest) {
        updateRequests.value.unshift(response.data.updateRequest)
      }
      // AC010 sync: pull fresh lock state so the UI knows the lock was acquired
      // or extended by this submit.
      try { await fetchInlineEditLockStatus(siteId) } catch (_) { /* best-effort */ }
      return response.data
    } catch (error) {
      console.error('Failed to submit inline edits:', error)
      _captureLockConflict(error, 'inline_edits')
      throw error
    }
  }

  const confirmInlineEdits = async (siteId) => {
    try {
      const response = await api.post(`/sites/${siteId}/inline-edits/confirm`)
      if (response.data?.message) toast.success(response.data.message)
      // AC010 sync: drained release after confirm — re-fetch authoritative state.
      try { await fetchInlineEditLockStatus(siteId) } catch (_) { /* best-effort */ }
      return response.data
    } catch (error) {
      console.error('Failed to confirm inline edits:', error)
      _captureLockConflict(error, 'confirm')
      throw error
    }
  }

  const undoInlineEdit = async (siteId) => {
    try {
      const response = await api.post(`/sites/${siteId}/inline-edits/undo`)
      if (response.data?.message) toast.success(response.data.message)
      // AC010 sync: refresh authoritative lock state after a successful undo
      // so the UI reflects the drained release (no more pending edits).
      try { await fetchInlineEditLockStatus(siteId) } catch (_) { /* best-effort */ }
      return response.data
    } catch (error) {
      console.error('Failed to undo inline edit:', error)
      _captureLockConflict(error, 'undo')
      throw error
    }
  }

  // Discard all pending inline-edit drafts and drain the (usually stale) edit
  // lock — the "Reset" recovery for the "No active edit session" stuck state.
  const resetInlineEdits = async (siteId) => {
    try {
      const response = await api.post(`/sites/${siteId}/inline-edits/reset`)
      if (response.data?.message) toast.success(response.data.message)
      try { await fetchInlineEditLockStatus(siteId) } catch (_) { /* best-effort */ }
      return response.data
    } catch (error) {
      console.error('Failed to reset edit session:', error)
      _captureLockConflict(error, 'reset')
      throw error
    }
  }

  // AC009 / AC010: fetch the authoritative edit-lock state for a site. Used
  // on mount, after every successful mutation, and after every 409 conflict.
  // Non-destructive on failure (keeps prior status visible so the UI doesn't
  // flicker to "unlocked" when the network is flaky).
  const fetchInlineEditLockStatus = async (siteId) => {
    if (!siteId) return null
    inlineEditLockLoading.value = true
    inlineEditLockError.value = null
    try {
      const response = await api.get(`/sites/${siteId}/edit-lock`)
      const { locked, isOwner, pendingCount, lock } = response.data || {}
      inlineEditLockStatus.value = { locked: !!locked, isOwner: !!isOwner, pendingCount: pendingCount || 0, lock: lock || null }
      return inlineEditLockStatus.value
    } catch (error) {
      console.error('Failed to fetch edit lock status:', error)
      inlineEditLockError.value = {
        code: error?.response?.data?.code || 'edit_lock_status_unavailable',
        message: error?.response?.data?.error || error?.message || 'Could not fetch lock status.'
      }
      // Intentionally do NOT clear inlineEditLockStatus.value — UI must keep
      // showing the last known authoritative state under transient failure.
      return inlineEditLockStatus.value
    } finally {
      inlineEditLockLoading.value = false
    }
  }

  // AC012 helper: extract the relevant lock-conflict shape from a 409 response
  // so the UI can render an actionable message ("Locked by alice@... — try
  // again at 11:42") instead of a generic error.
  const _captureLockConflict = (error, entryPoint) => {
    const data = error?.response?.data || {}
    if (data?.code !== 'edit_lock_conflict' && data?.code !== 'edit_lock_missing'
        && data?.code !== 'edit_lock_action_race' && data?.code !== 'edit_lock_stale') {
      return
    }
    lastLockConflict.value = {
      code: data.code,
      heldByEmail: data.details?.heldByEmail || null,
      heldByUserId: data.details?.heldByUserId || null,
      expiresAt: data.details?.expiresAt || null,
      acquiredAt: data.details?.acquiredAt || null,
      entryPoint,
      message: data.error || 'This site is being edited by another editor.'
    }
  }
  const clearLockConflict = () => { lastLockConflict.value = null }

  // Submit a social-post generation request. Uses the shared authenticated
  // api client so auth/session headers and base URL handling match every
  // other editor workflow. Returns the structured envelope produced by the
  // backend SocialPostService; the caller is responsible for surfacing the
  // variants and for treating malformed responses as failure (AC012/AC013).
  const REQUIRED_SOCIAL_VARIANT_COUNT = 3
  const validateSocialPostResponse = (data, requestedPlatforms = []) => {
    if (!data || typeof data !== 'object') return 'malformed_response'
    if (!Array.isArray(data.platforms)) return 'missing_platforms_array'
    if (!data.variantsByPlatform || typeof data.variantsByPlatform !== 'object') return 'missing_variants_block'
    const platforms = Array.isArray(requestedPlatforms) && requestedPlatforms.length
      ? requestedPlatforms
      : data.platforms
    const expectedCount = typeof data.variantsPerPlatform === 'number' ? data.variantsPerPlatform : REQUIRED_SOCIAL_VARIANT_COUNT
    for (const platformId of platforms) {
      const variants = data.variantsByPlatform[platformId]
      if (!Array.isArray(variants)) return `missing_variants_for_${platformId}`
      if (variants.length !== expectedCount) return `wrong_variant_count_for_${platformId}`
      for (const v of variants) {
        if (!v || typeof v.text !== 'string' || !v.text.trim()) return `empty_variant_for_${platformId}`
        if (v.contentType && v.contentType !== 'text') return `non_text_variant_for_${platformId}`
      }
    }
    return null
  }

  // Ask-mode submission. Posts to the dedicated /sites/:id/ask endpoint so
  // the Change-mode update path is never touched (Ask-mode AC002/AC003).
  // Uses the shared `api` client so auth/session headers (Bearer JWT) flow
  // through the same interceptors as every other editor action. Validates
  // the response envelope so callers can treat malformed payloads as a
  // failure rather than as a silent success (Ask-mode AC009).
  const validateAskResponse = (data) => {
    if (!data || typeof data !== 'object') return 'malformed_response'
    if (data.success !== true) return 'unsuccessful_response'
    const answer = data.answer
    if (!answer || typeof answer !== 'object') return 'missing_answer_envelope'
    if (answer.kind !== 'bwai/ask_answer') return 'wrong_answer_kind'
    if (typeof answer.question !== 'string' || !answer.question.trim()) return 'missing_answer_question'
    if (typeof answer.answer !== 'string' || !answer.answer.trim()) return 'missing_answer_text'
    const askRequest = data.askRequest
    if (!askRequest || typeof askRequest !== 'object') return 'missing_ask_request'
    if (askRequest.request_type !== 'question') return 'wrong_request_type'
    if (askRequest.status !== 'answered') return 'wrong_request_status'
    return null
  }

  const submitAskRequest = async (siteId, payload) => {
    try {
      const response = await api.post(`/sites/${siteId}/ask`, payload)
      const data = response?.data || {}
      const failureCode = validateAskResponse(data)
      if (failureCode) {
        const error = new Error('Ask response did not match required format.')
        error.code = failureCode
        error.normalizedSource = 'response_validation'
        throw error
      }
      return data
    } catch (error) {
      console.error('Failed to submit ask question:', error)
      throw error
    }
  }

  const regenerateSocialPostImage = async (siteId, { platforms, prompt, imageStyle = 'basic', postId = null }) => {
    try {
      // Fire-and-forget: the backend acks with 202 and streams the finished
      // image over SSE (`social_image_regenerated`). We no longer block on the
      // slow Codex generation here — that's what caused the 30s axios timeout.
      await api.post(`/sites/${siteId}/social-posts/generate-image`, { platforms, prompt, imageStyle, postId })
      return {}
    } catch (error) {
      console.error('Failed to regenerate social post image:', error)
      throw error
    }
  }

  const fetchSocialPost = async (siteId, postId) => {
    try {
      const response = await api.get(`/sites/${siteId}/social-posts/${postId}`)
      return response?.data?.post || null
    } catch (error) {
      return null
    }
  }

  const fetchSocialPosts = async (siteId, { limit = 20 } = {}) => {
    try {
      const response = await api.get(`/sites/${siteId}/social-posts`, { params: { limit } })
      return response?.data?.posts || []
    } catch (error) {
      console.error('Failed to fetch social posts:', error)
      return []
    }
  }

  const generateSocialPosts = async (siteId, payload) => {
    try {
      const response = await api.post(`/sites/${siteId}/social-posts`, payload)
      const data = response?.data || {}
      const requestedPlatforms = Array.isArray(payload?.platforms) ? payload.platforms : []
      const failureCode = validateSocialPostResponse(data, requestedPlatforms)
      if (failureCode) {
        const error = new Error('Generated social posts did not match required format.')
        error.code = failureCode
        error.normalizedSource = 'response_validation'
        throw error
      }
      return data
    } catch (error) {
      console.error('Failed to generate social posts:', error)
      throw error
    }
  }

  const provideFeedback = async (siteId, updateId, feedbackData) => {
    try {
      const response = await api.post(`/sites/${siteId}/feedback/${updateId}`, feedbackData)
      
      // Update the request status in our local state
      const requestIndex = updateRequests.value.findIndex(req => req.id === updateId)
      if (requestIndex !== -1) {
        updateRequests.value[requestIndex].status = response.data.status
        updateRequests.value[requestIndex].feedback = feedbackData
      }
      
      toast.success(response.data.message)
      return response.data
    } catch (error) {
      console.error('Failed to provide feedback:', error)
      throw error
    }
  }

  // Resume an update request that paused with awaiting_clarification status.
  // Body shape: { answer?: string, proceedAnyway?: boolean } — both can be
  // present; proceedAnyway always wins.
  const provideClarification = async (siteId, updateId, { answer = null, proceedAnyway = false } = {}) => {
    try {
      const response = await api.post(`/sites/${siteId}/clarify/${updateId}`, {
        answer,
        proceedAnyway
      })
      const requestIndex = updateRequests.value.findIndex(req => req.id === updateId)
      if (requestIndex !== -1) {
        updateRequests.value[requestIndex].status = response.data.status
      }
      return response.data
    } catch (error) {
      console.error('Failed to provide clarification:', error)
      throw error
    }
  }

  const fetchUpdateRequests = async (siteId, options = {}) => {
    try {
      const response = await api.get(`/sites/${siteId}/updates`, { params: options })
      updateRequests.value = response.data.updates
      return response.data
    } catch (error) {
      console.error('Failed to fetch update requests:', error)
      throw error
    }
  }

  // Lazy diff fetch for the conversation log — used to render the inline
  // hunk view under a Change turn when the user expands it. The backend
  // returns { hasContent, reason?, files: [...] } so the caller can show
  // the right state without re-parsing.
  const fetchUpdateDiff = async (siteId, updateId) => {
    try {
      const response = await api.get(`/sites/${siteId}/updates/${updateId}/diff`)
      return response.data
    } catch (error) {
      console.error('Failed to fetch update diff:', error)
      throw error
    }
  }

  const fetchAllUpdateRequests = async (options = {}) => {
    try {
      const response = await api.get('/updates', { params: options })
      return response.data
    } catch (error) {
      console.error('Failed to fetch all update requests:', error)
      throw error
    }
  }

  const cliOutputs = ref({})

  const activityStorageKey = (siteId) => `${ACTIVITY_STORAGE_PREFIX}${siteId}`

  const serializeActivityMessage = (eventType, data = {}) => {
    if (typeof data === 'string') return data
    return data?.message || data?.error || data?.summary || JSON.stringify(data || {})
  }

  const normalizeActivityEntry = (siteId, eventType, data = {}) => {
    const eventData = data?.data && typeof data.data === 'object'
      ? { ...data.data, ...data }
      : data
    const plainMessage = eventData?.message || eventData?.summary || null
    const timestamp = eventData?.timestamp || eventData?.createdAt || Date.now()
    const serialized = serializeActivityMessage(eventType, eventData)

    return {
      eventId: eventData?.eventId || eventData?.id || null,
      siteId,
      eventType,
      plainMessage,
      stream: eventData?.type || eventType,
      message: `[${eventType}] ${serialized || eventType}`,
      timestamp,
      progress: eventData?.progress ?? eventData?.percent ?? eventData?.percentage ?? null,
      updateRequestId: eventData?.updateRequestId || eventData?.update_request_id || null,
      sessionId: eventData?.sessionId || eventData?.session_id || null,
      // Preserve the question text for awaiting_clarification events so the
      // conversation log can render it without a second round-trip.
      question: eventData?.question || null
    }
  }

  const persistCliOutputs = (siteId) => {
    if (typeof localStorage === 'undefined' || !siteId) return
    try {
      const entries = (cliOutputs.value[siteId] || []).slice(-MAX_ACTIVITY_ENTRIES)
      localStorage.setItem(activityStorageKey(siteId), JSON.stringify(entries))
    } catch (error) {
      console.warn('Failed to persist site activity:', error)
    }
  }

  const loadPersistedCliOutputs = (siteId) => {
    if (typeof localStorage === 'undefined' || !siteId) return []
    try {
      const raw = localStorage.getItem(activityStorageKey(siteId))
      if (!raw) return []
      const entries = JSON.parse(raw)
      if (!Array.isArray(entries)) return []
      cliOutputs.value[siteId] = entries.slice(-MAX_ACTIVITY_ENTRIES)
      return cliOutputs.value[siteId]
    } catch (error) {
      console.warn('Failed to load persisted site activity:', error)
      return []
    }
  }

  const activityEntryKey = (entry) => {
    if (entry.eventId) return `id:${entry.eventId}`
    return [
      entry.eventType || entry.stream || 'event',
      entry.timestamp || '',
      entry.message || entry.plainMessage || ''
    ].join('|')
  }

  const mergeCliOutputs = (siteId, entries = []) => {
    if (!siteId || !Array.isArray(entries) || entries.length === 0) return []
    const existing = cliOutputs.value[siteId] || []
    const byKey = new Map(existing.map((entry) => [activityEntryKey(entry), entry]))
    for (const entry of entries) {
      if (!entry?.message) continue
      byKey.set(activityEntryKey(entry), entry)
    }
    cliOutputs.value[siteId] = Array.from(byKey.values())
      .sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
      .slice(-MAX_ACTIVITY_ENTRIES)
    persistCliOutputs(siteId)
    return cliOutputs.value[siteId]
  }

  const appendCliOutput = (siteId, entry) => {
    if (!entry?.message) return
    mergeCliOutputs(siteId, [entry])
  }

  const fetchSiteActivity = async (siteId, options = {}) => {
    try {
      const response = await api.get(`/sites/${siteId}/activity`, {
        params: { limit: options.limit || 100 }
      })
      const entries = (response.data?.events || []).map((event) => normalizeActivityEntry(siteId, event.eventType, {
        ...(event.data || {}),
        id: event.id,
        eventId: event.id,
        message: event.summary || event.data?.message || event.eventType,
        summary: event.summary,
        createdAt: event.createdAt,
        updateRequestId: event.updateRequestId,
        sessionId: event.sessionId
      }))
      mergeCliOutputs(siteId, entries)
      return entries
    } catch (error) {
      console.error('Failed to fetch site activity:', error)
      throw error
    }
  }

  // ─── Team management ─────────────────────────────────────────────────────

  const teamMembers = ref([])
  const teamInvites = ref([])

  const fetchTeam = async () => {
    const response = await api.get('/team')
    teamMembers.value = Array.isArray(response.data.members) ? response.data.members : []
    teamInvites.value = Array.isArray(response.data.invites) ? response.data.invites : []
    return { members: teamMembers.value, invites: teamInvites.value }
  }

  const inviteTeammate = async ({ email, role, name }) => {
    const response = await api.post('/team/invites', { email, role, name })
    return response.data?.invitation
  }

  const cancelTeamInvite = async (invitationId) => {
    await api.delete(`/team/invites/${invitationId}`)
    return invitationId
  }

  const changeTeamRole = async (memberId, role) => {
    const response = await api.patch(`/team/members/${memberId}/role`, { role })
    return response.data?.member
  }

  const changeTeamStatus = async (memberId, status) => {
    const response = await api.patch(`/team/members/${memberId}/status`, { status })
    return response.data?.member
  }

  const removeTeamMember = async (memberId) => {
    await api.delete(`/team/members/${memberId}`)
    return memberId
  }

  // ─── Notification preferences ────────────────────────────────────────────

  const notificationPreferences = ref(null)
  const notificationCategories = ref([])
  const transactionalCategories = ref([])

  const fetchNotificationPreferences = async () => {
    const response = await api.get('/notifications/preferences')
    notificationPreferences.value = response.data?.preferences || {}
    notificationCategories.value = Array.isArray(response.data?.categories)
      ? response.data.categories : []
    transactionalCategories.value = Array.isArray(response.data?.transactionalCategories)
      ? response.data.transactionalCategories : []
    return {
      preferences: notificationPreferences.value,
      categories: notificationCategories.value,
      transactionalCategories: transactionalCategories.value,
    }
  }

  const updateNotificationPreferences = async (partial) => {
    const response = await api.patch('/notifications/preferences', partial)
    notificationPreferences.value = response.data?.preferences || {}
    notificationCategories.value = Array.isArray(response.data?.categories)
      ? response.data.categories : notificationCategories.value
    return notificationPreferences.value
  }

  const setUser = (userData, tenantData) => {
    user.value = userData
    tenant.value = tenantData
    import('../plugins/amplitude').then(({ identifyUser }) => identifyUser(userData, tenantData))
  }

  const clearAuth = () => {
    user.value = null
    tenant.value = null
    localStorage.removeItem('bwai_token')
    localStorage.removeItem('bwai_user')
    localStorage.removeItem('bwai_tenant')
    delete api.defaults.headers.common['Authorization']
    import('../plugins/amplitude').then(({ resetUser }) => resetUser())
  }

  const initAuth = () => {
    const token = localStorage.getItem('bwai_token')
    const userData = localStorage.getItem('bwai_user')
    const tenantData = localStorage.getItem('bwai_tenant')
    
    if (token && userData && tenantData) {
      try {
        user.value = JSON.parse(userData)
        tenant.value = JSON.parse(tenantData)
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`
        import('../plugins/amplitude').then(({ identifyUser }) => identifyUser(user.value, tenant.value))
        return true
      } catch (error) {
        console.error('Failed to parse stored auth data:', error)
        clearAuth()
        return false
      }
    }
    return false
  }

  const connectSSE = (siteId, onMessage) => {
    if (sseConnection.value) {
      sseConnection.value.close()
    }

    const token = localStorage.getItem('bwai_token')
    if (!token) {
      console.error('No authentication token available for SSE connection')
      return null
    }

    const eventSource = new EventSource(`/api/bwai/sites/${siteId}/stream?token=${encodeURIComponent(token)}`)
    sseConnection.value = eventSource

    eventSource.onopen = () => {
      console.log('✅ BWAI SSE CONNECTION ESTABLISHED for site:', siteId)
    }

    const handleEvent = (event) => {
      try {
       if (!event.data) return
       const data = JSON.parse(event.data)
       console.log(`📡 BWAI SSE Event [${event.type}]:`, data)
       
       // Update local state based on event
       if (event.type === 'creation_started') {
         updateSiteStatus(siteId, 'creating')
       } else if (event.type === 'creation_completed') {
         updateSiteStatus(siteId, 'active')
       } else if (event.type === 'creation_failed') {
         updateSiteStatus(siteId, 'failed')
       }

       const previewUrl = data.previewUrl || data.preview_url
       if (previewUrl) {
         updateSitePreviewUrl(siteId, previewUrl)
       }

        appendCliOutput(siteId, normalizeActivityEntry(siteId, event.type, data))
        
        // Call custom message handler
        if (onMessage) {
          onMessage(event.type, data)
        }
      } catch (error) {
        console.error('BWAI SSE message parse error:', error)
      }
    }

    const ALL_EVENTS = [
      'connected',
      // lifecycle
      'creation_started', 'creation_completed', 'creation_failed',
      // inspiration workflow nodes
      'template_clone_started', 'template_clone_completed',
      'style_application_started', 'style_application_completed',
      'style_global_started', 'style_global_completed',
      'style_page_started', 'style_page_completed',
      'style_eval_started', 'style_eval_completed',
      'content_generation_started', 'content_generation_completed',
      'content_eval_started', 'content_eval_completed',
      // other creation types
      'component_generation_started', 'component_generation_completed',
      'repo_import_started', 'repo_import_completed',
      'inspiration_analysis_started', 'inspiration_analysis_completed',
      'brand_extraction_started', 'brand_extraction_completed',
      // update workflow
      'implementation_started', 'implementation_completed', 'implementation_failed',
      'implementation_progress',
      'classification_completed', 'awaiting_feedback', 'awaiting_upgrade',
      'cli_execution_started', 'cli_execution_progress', 'cli_execution_error',
      'ai_task_started', 'ai_task_completed', 'ai_task_failed',
      'cagent_implementation_output',
      'preview_deploying', 'preview_ready', 'preview_failed', 'preview_warming', 'preview_restart_failed',
      'template_created', 'sandbox_published', 'published', 'publish_failed', 'reverted',
      // site_clone workflow nodes
      'clone_capture_started', 'clone_capture_completed', 'clone_conversion_started', 'clone_conversion_completed',
      'html_import_conversion_started', 'html_import_conversion_completed', 'html_import_conversion_failed',
      // creation feedback pause/resume events
      'awaiting_creation_feedback', 'creation_feedback_approved', 'creation_feedback_rejected',
      // non-destructive guardrails + inline edit lifecycle (BUI-US-34)
      'destructive_update_blocked',
      'inline_edit_submitted', 'inline_edit_rejected', 'inline_edit_conflict',
      // Ask mode lifecycle
      'question_answered',
      'update_failed',
      'error',
    ]
    ALL_EVENTS.forEach(type => eventSource.addEventListener(type, handleEvent))

    eventSource.onerror = (error) => {
      console.error('BWAI SSE connection error:', error)
      // Will auto-reconnect
    }

    eventSource.onclose = () => {
      console.log('BWAI SSE connection closed')
    }

    return eventSource
  }

  const disconnectSSE = () => {
    if (sseConnection.value) {
      sseConnection.value.close()
      sseConnection.value = null
    }
  }

  const updateSiteStatus = (siteId, status) => {
    const siteIndex = sites.value.findIndex(site => site.id === siteId)
    if (siteIndex !== -1) {
      sites.value[siteIndex].status = status
    }
    
    if (currentSite.value?.id === siteId) {
      currentSite.value.status = status
    }
  }

 const updateSitePreviewUrl = (siteId, previewUrl) => {
   if (!previewUrl) return

    const siteIndex = sites.value.findIndex(site => site.id === siteId)
    if (siteIndex !== -1) {
      sites.value[siteIndex].preview_url = previewUrl
    }

    if (currentSite.value?.id === siteId) {
      currentSite.value.preview_url = previewUrl
   }
  }

  const deleteSite = async (siteId) => {
    try {
      await api.delete(`/sites/${siteId}`)
      
      // Remove from sites array
      const siteIndex = sites.value.findIndex(site => site.id === siteId)
      if (siteIndex !== -1) {
        sites.value.splice(siteIndex, 1)
      }
      
      // Clear current site if it was the deleted one
      if (currentSite.value?.id === siteId) {
        currentSite.value = null
      }
      
      toast.success('Site deleted successfully')
      return true
    } catch (error) {
      console.error('Failed to delete site:', error)
      toast.error('Failed to delete site')
      throw error
    }
  }

  // ── Credits / tiered pricing ───────────────────────────────────────────────
  const pricingPolicy = ref(null)        // { id, tiers, credit_limit, ... } | null
  const pricingPolicyLoading = ref(false)
  const pricingPolicySaving = ref(false)
  const pricingPolicyError = ref(null)

  const creditBalance = ref(null)        // { consumed, limit, available } | null
  const creditBalanceLoading = ref(false)
  const creditBalanceError = ref(null)

  const fetchPricingPolicy = async () => {
    pricingPolicyLoading.value = true
    pricingPolicyError.value = null
    try {
      const res = await api.get('/credits/policy')
      pricingPolicy.value = res.data.policy
    } catch (err) {
      if (err?.response?.status === 404) {
        pricingPolicy.value = null
      } else {
        pricingPolicyError.value = err?.response?.data?.error || 'Could not load pricing policy'
      }
    } finally {
      pricingPolicyLoading.value = false
    }
  }

  const savePricingPolicy = async (tiers, creditLimit) => {
    pricingPolicySaving.value = true
    pricingPolicyError.value = null
    try {
      const res = await api.post('/credits/policy', { tiers, credit_limit: creditLimit ?? null })
      pricingPolicy.value = res.data.policy
      return res.data.policy
    } catch (err) {
      const e = err?.response?.data
      pricingPolicyError.value = e?.validationErrors?.join(', ') || e?.error || 'Could not save pricing policy'
      throw err
    } finally {
      pricingPolicySaving.value = false
    }
  }

  const fetchCreditBalance = async () => {
    creditBalanceLoading.value = true
    creditBalanceError.value = null
    try {
      const res = await api.get('/credits/balance')
      creditBalance.value = { consumed: res.data.consumed, limit: res.data.limit, available: res.data.available }
    } catch (err) {
      creditBalanceError.value = err?.response?.data?.error || 'Could not load credit balance'
    } finally {
      creditBalanceLoading.value = false
    }
  }

  const forecastCredits = async (usageAmount) => {
    const res = await api.post('/credits/forecast', { usage_amount: usageAmount })
    return res.data
  }

  const siteCapacity = ref(null) // { current, limit, available } | null
  const siteCapacityLoading = ref(false)
  const siteCapacityError = ref(null)

  const fetchSiteCapacity = async () => {
    siteCapacityLoading.value = true
    siteCapacityError.value = null
    try {
      const res = await api.get('/sites/capacity')
      siteCapacity.value = res.data
    } catch (err) {
      siteCapacityError.value = err?.response?.data?.error || 'Could not load site capacity'
    } finally {
      siteCapacityLoading.value = false
    }
  }

  return {
    // State
    sites,
    currentSite,
    updateRequests,
    loading,
    sseConnection,
    user,
    tenant,
    cliOutputs,
    
    // Computed
    activeSites,
    creatingSites,
    
    // Actions
    fetchSites,
    fetchSite,
    refreshSitePreview,
    preflightRepoImport,
    discoverClonePages,
    createSite,
    createHtmlImportSite,
    createUpdateRequest,
    submitInlineEdits,
    fetchPageSeo,
    savePageSeo,
    confirmInlineEdits,
    undoInlineEdit,
    resetInlineEdits,
    submitAskRequest,
    fetchSocialPost,
    fetchSocialPosts,
    regenerateSocialPostImage,
    generateSocialPosts,
    provideFeedback,
    provideClarification,
    fetchUpdateRequests,
    fetchUpdateDiff,
    fetchAllUpdateRequests,
    connectSSE,
    disconnectSSE,
    updateSiteStatus,
    setUser,
    clearAuth,
    initAuth,
    teamMembers,
    teamInvites,
    fetchTeam,
    inviteTeammate,
    cancelTeamInvite,
    changeTeamRole,
    changeTeamStatus,
    removeTeamMember,
    notificationPreferences,
    notificationCategories,
    transactionalCategories,
    fetchNotificationPreferences,
    updateNotificationPreferences,
    appendCliOutput,
    loadPersistedCliOutputs,
    fetchSiteActivity,
    deleteSite,
    // Release history + rollback (Commit-Selectable Release History story)
    releaseHistory,
    releaseHistoryLoading,
    releaseHistoryError,
    activeRollbackJob,
    fetchReleaseHistory,
    requestRollback,
    fetchReleaseJob,
    // Edit-lock UI state + actions (Edit Locking for Concurrent Update Safety)
    inlineEditLockStatus,
    inlineEditLockLoading,
    inlineEditLockError,
    lastLockConflict,
    fetchInlineEditLockStatus,
    clearLockConflict,
    // Site capacity
    siteCapacity,
    siteCapacityLoading,
    siteCapacityError,
    fetchSiteCapacity,
    // Credits / tiered pricing
    pricingPolicy,
    pricingPolicyLoading,
    pricingPolicySaving,
    pricingPolicyError,
    creditBalance,
    creditBalanceLoading,
    creditBalanceError,
    fetchPricingPolicy,
    savePricingPolicy,
    fetchCreditBalance,
    forecastCredits,
  }
})
